import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Save } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { apiError } from "../../pages/SowEmails";

const API = process.env.REACT_APP_BACKEND_URL;

export function SettingsPanel({ sender, alertList, onSaved }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setValue((alertList?.emails || []).join(", ")); }, [alertList]);
  const dirty = alertList && value.trim() !== (alertList.emails || []).join(", ");

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.put(`${API}/api/sow-emails/settings/alert-list`, { emails: value.split(",").map((s) => s.trim()).filter(Boolean) });
      onSaved(data);
      toast.success("Alert list saved");
    } catch (e) {
      setError(apiError(e, "Could not save the alert list"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="sow-emails-settings">
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1">SOW alert list</p>
        <p className="text-xs text-stone-500 mb-2">Copied on every email the system sends and the address replies come back to. Separate with commas.</p>
        <div className="flex items-center gap-2">
          <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="name@medicysltd.co.uk, other@medicysltd.co.uk" data-testid="sow-emails-alert-list" />
          <Button onClick={save} disabled={!dirty || busy} className="bg-[#1B4F8A] hover:bg-[#173f6e] shrink-0" data-testid="sow-emails-alert-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
        </div>
        {error && <p className="text-sm text-rose-700 mt-2" data-testid="sow-emails-alert-error">{error}</p>}
        {alertList && (
          <p className="text-xs text-stone-500 mt-2" data-testid="sow-emails-alert-source">
            {alertList.source === "app"
              ? `Saved in the app by ${alertList.updated_by} on ${new Date(alertList.updated_at).toLocaleString("en-GB")}.`
              : "Currently taken from the server setting SOW_ALERT_EMAILS; saving here takes over."}
          </p>
        )}
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1">Sent as</p>
        {sender && (
          <div className="text-sm text-stone-700 flex flex-col gap-1.5" data-testid="sow-emails-sender">
            <p>
              {sender.from}
              <span className="text-stone-500"> · via {sender.transport === "microsoft365" ? "Microsoft 365" : "Resend"}</span>
              {sender.ok_at && <span className="text-emerald-700"> · last sent {new Date(sender.ok_at).toLocaleString("en-GB")}</span>}
            </p>
            {sender.fallback_at && (
              <p className="flex items-start gap-2 text-amber-800 text-xs" data-testid="sow-emails-sender-fallback">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Microsoft 365 refused a send on {new Date(sender.fallback_at).toLocaleString("en-GB")} ({sender.fallback_error}); that email went out through Resend as {sender.fallback}.</span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
